import React from 'react';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_WORKFLOWS_SKIP_CHECKLIST_SECTIONS } from 'constants/featureFlags';
import Modal from 'components/shared/DesignSystem/Modal';
import styled from 'styled-components';
import { Button, InputRadioGroup, Switch } from '@companycam/slab-web';
import { translate } from 'translations/i18n';

const StyledModal = styled(Modal)`
  width: 553px;
  display: flex;
  flex-direction: column;
`;

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
  margin-bottom: var(--cc_size_spacing_l);
`;

const ExportModal = ({
  isOpen,
  onRequestClose,
  onExportClick,
  toggleDispatch,
  toggleState,
  loading,
}) => {
  const flags = useFlags();
  const skipSectionsEnabled = flags[LD_WORKFLOWS_SKIP_CHECKLIST_SECTIONS];

  const {
    includePhotos,
    includePhotoDetails,
    photosPerRow,
    saveToProjectDocuments,
    includeSkippedSections,
  } = toggleState;

  return (
    <StyledModal
      isOpen={isOpen}
      isClosable
      onRequestClose={onRequestClose}
      title={translate('projects.taskList.taskListHeader.exportToPdf')}
      className="modal-content-wrap"
    >
      <p className="instructions">
        {translate('projects.taskList.exportPdf.description')}
      </p>

      <SwitchContainer>
        <Switch
          id="includePhotos"
          label={translate('projects.taskList.exportPdf.includePhotosToggle')}
          pressed={includePhotos}
          onClick={() => toggleDispatch({ type: 'includePhotos' })}
          disabled={loading}
          layout="group"
        />

        <Switch
          id="includePhotoDescription"
          label={translate(
            'projects.taskList.exportPdf.includePhotoDescriptionToggle',
          )}
          pressed={includePhotoDetails}
          onClick={() => toggleDispatch({ type: 'includePhotoDetails' })}
          disabled={loading || !includePhotos}
          layout="group"
        />

        {skipSectionsEnabled ? (
          <Switch
            id="includeSkippedSections"
            label={translate(
              'projects.taskList.exportPdf.includeSkippedSectionsToggle',
            )}
            pressed={includeSkippedSections}
            onClick={() => toggleDispatch({ type: 'includeSkippedSections' })}
            disabled={loading}
            layout="group"
          />
        ) : null}

        <Switch
          id="saveToProjectDocuments"
          label={translate(
            'projects.taskList.exportPdf.saveToProjectDocumentsToggle',
          )}
          name="saveToProjectDocuments"
          pressed={saveToProjectDocuments}
          onClick={() => toggleDispatch({ type: 'saveToProjectDocuments' })}
          disabled={loading}
          layout="group"
        />
      </SwitchContainer>

      <InputRadioGroup
        layout="inline"
        selectedValue={photosPerRow}
        id="photosPerRow"
        label={translate('projects.taskList.exportPdf.photosPerRowToggle')}
        options={[
          {
            label: '2',
            value: 2,
          },
          {
            label: '3',
            value: 3,
          },
        ]}
        onChange={() => toggleDispatch({ type: 'photosPerRow' })}
        disabled={loading || !includePhotos}
      />

      <div className="btn-container">
        <Button color="primary" onClick={onExportClick} loading={loading}>
          {translate('projects.taskList.exportPdf.exportBtn')}
        </Button>
      </div>
    </StyledModal>
  );
};

ExportModal.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  onExportClick: PropTypes.func,
  toggleState: PropTypes.object,
  toggleDispatch: PropTypes.func,
  loading: PropTypes.bool,
};

export default ExportModal;
